import styled from "@emotion/styled";
import { useCallback, type MouseEventHandler } from "react";

import { Button, FONT_WEIGHT_SEMI_BOLD, useSearch } from "@skilitics/designer-ui-components";
import { useFormatMessage, type FormatMessages } from "@skilitics/localization";

import { useModuleUrl, type Status } from "../shared";

export const ActionButtonsContainer = styled.div`
  position: absolute;
  top: 0;
  height: 100%;

  display: flex;
  justify-content: center;
  align-items: center;
  gap: 22px;
  z-index: 1;

  width: 100%;
  font-size: var(--font-size-small);
  font-weight: ${FONT_WEIGHT_SEMI_BOLD};

  opacity: 0;

  pointer-events: none;
`;

const StyledButton = styled(Button)`
  display: flex;
  justify-content: space-around;
  align-items: center;

  width: 100px;
  height: 36px;

  font-size: 15px;
  font-weight: ${FONT_WEIGHT_SEMI_BOLD};

  border-radius: 10px;

  &.Mui-disabled {
    background-color: ${({ theme }) => theme.primary};
  }
`;

type Props = {
  moduleId: string;
  disabled?: boolean;
  setBusy: (busy: Status) => void;
};

export const ActionButtons = ({ moduleId, disabled, setBusy }: Props) => {
  const formatMessage = useFormatMessage(formatStrings);
  const moduleUrl = useModuleUrl(moduleId);

  const { reset } = useSearch();

  const handleOpen: MouseEventHandler = useCallback(() => {
    setBusy("opening");
    reset();
  }, [setBusy, reset]);

  return (
    <ActionButtonsContainer data-test="module-actions-container">
      <StyledButton
        href={moduleUrl}
        variant="contained"
        disabled={disabled}
        onClick={handleOpen}
        data-test="open-module-button"
      >
        {formatMessage("open")}
      </StyledButton>
    </ActionButtonsContainer>
  );
};

const formatStrings = {
  open: {
    en: "Open",
  },
} satisfies FormatMessages;
